<script setup>
import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import axios from 'axios';

const { can, isAdminOrSuperAdmin } = usePermissions();
// Borrar columna: admin/super o quien tenga stages.delete (los agentes de
// migración tienen stages.edit pero NO delete → no ven la zona peligrosa).
const canDeleteStage = computed(
  () => isAdminOrSuperAdmin.value || can('stages.delete')
);

const props = defineProps({
  stage: { type: Object, required: true },
  pipeline: { type: Object, required: true },
  allStages: { type: Array, required: true },
  accountId: { type: [String, Number], required: true },
  totalLeads: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'edit', 'delete', 'reordered', 'import-csv', 'export-csv']);

const currentIndex = computed(() =>
  props.allStages.findIndex(s => s.id === props.stage.id)
);
const totalStages = computed(() => props.allStages.length);
const isFirst = computed(() => currentIndex.value <= 0);
const isLast = computed(() => currentIndex.value >= totalStages.value - 1);

const leftNeighbor = computed(() =>
  isFirst.value ? null : props.allStages[currentIndex.value - 1]
);
const rightNeighbor = computed(() =>
  isLast.value ? null : props.allStages[currentIndex.value + 1]
);

async function moveToPosition(newPosition) {
  const url = `/api/v1/accounts/${props.accountId}/pipelines/${props.pipeline.id}/stages/${props.stage.id}`;
  try {
    await axios.patch(url, { stage: { position: newPosition } });
    useAlert('✓ Columna reordenada');
    emit('reordered');
    emit('close');
  } catch (e) {
    useAlert(e?.response?.data?.message || 'Error al reordenar columna');
  }
}

function moveLeft() {
  if (isFirst.value) return;
  moveToPosition(currentIndex.value - 1);
}
function moveRight() {
  if (isLast.value) return;
  moveToPosition(currentIndex.value + 1);
}
function moveToStart() {
  if (isFirst.value) return;
  moveToPosition(0);
}
function moveToEnd() {
  if (isLast.value) return;
  moveToPosition(totalStages.value - 1);
}

function onRename() { emit('edit'); emit('close'); }
function onColor() { emit('edit'); emit('close'); }
function onDelete() { emit('delete'); emit('close'); }
function onImportCsv() { emit('import-csv'); emit('close'); }
function onExportCsv() { emit('export-csv'); emit('close'); }
</script>

<template>
  <div class="csp-panel">
    <button class="csp-close" @click="$emit('close')">×</button>
    <div class="csp-head" :style="{ background: `linear-gradient(135deg, ${stage.color}22, transparent)` }">
      <div class="csp-eyebrow">Gestionar columna</div>
      <div class="csp-title">
        <div class="csp-color-dot" :style="{ background: stage.color }"></div>
        <span class="csp-title-name">{{ stage.name }}</span>
      </div>
      <div class="csp-sub">{{ totalLeads }} {{ totalLeads === 1 ? 'lead' : 'leads' }} en esta etapa</div>
      <div class="csp-position-badge">📍 Posición {{ currentIndex + 1 }} de {{ totalStages }}</div>
    </div>

    <div class="csp-section">
      <div class="csp-section-label">✏️ Editar</div>
      <div class="csp-item" @click="onRename">
        <div class="csp-item-icon">✏</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Renombrar columna</div>
          <div class="csp-item-sub">Cambia el título visible en el kanban</div>
        </div>
      </div>
      <div class="csp-item" @click="onColor">
        <div class="csp-item-icon csp-item-icon-color">🎨</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Cambiar color</div>
          <div class="csp-item-sub">Identificación visual rápida</div>
        </div>
      </div>
    </div>

    <div class="csp-divider"></div>

    <div class="csp-section">
      <div class="csp-section-label">↔️ Reordenar posición</div>
      <div
        class="csp-item"
        :class="{ 'csp-item-disabled': isFirst }"
        @click="moveLeft"
      >
        <div class="csp-item-icon">←</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Mover a la izquierda</div>
          <div class="csp-item-sub">{{ leftNeighbor ? `Intercambia con "${leftNeighbor.name}"` : 'Ya está en el inicio' }}</div>
        </div>
      </div>
      <div
        class="csp-item"
        :class="{ 'csp-item-disabled': isLast }"
        @click="moveRight"
      >
        <div class="csp-item-icon">→</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Mover a la derecha</div>
          <div class="csp-item-sub">{{ rightNeighbor ? `Intercambia con "${rightNeighbor.name}"` : 'Ya está en el final' }}</div>
        </div>
      </div>
      <div
        class="csp-item"
        :class="{ 'csp-item-disabled': isFirst }"
        @click="moveToStart"
      >
        <div class="csp-item-icon">⇤</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Mover al inicio</div>
          <div class="csp-item-sub">Posición 1 de {{ totalStages }}</div>
        </div>
      </div>
      <div
        class="csp-item"
        :class="{ 'csp-item-disabled': isLast }"
        @click="moveToEnd"
      >
        <div class="csp-item-icon">⇥</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Mover al final</div>
          <div class="csp-item-sub">Posición {{ totalStages }} de {{ totalStages }}</div>
        </div>
      </div>
    </div>

    <div class="csp-divider"></div>

    <div class="csp-section">
      <div class="csp-section-label">📥 Importar / exportar leads</div>
      <div class="csp-item" @click="onImportCsv">
        <div class="csp-item-icon" style="background:rgba(99,102,241,0.18);border-color:rgba(99,102,241,0.40);color:#a5b4fc">📥</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Importar leads (CSV)</div>
          <div class="csp-item-sub">Desde Kommo u otro CRM · mapeo de columnas</div>
        </div>
      </div>
      <div
        class="csp-item"
        :class="{ 'csp-item-disabled': totalLeads === 0 }"
        @click="totalLeads > 0 && onExportCsv()"
      >
        <div class="csp-item-icon" style="background:rgba(34,197,94,0.18);border-color:rgba(34,197,94,0.40);color:#6ee7b7">📤</div>
        <div class="csp-item-info">
          <div class="csp-item-title">Exportar leads (CSV)</div>
          <div class="csp-item-sub">
            {{ totalLeads === 0 ? 'Sin leads que exportar' : `Descargar los ${totalLeads} leads de esta etapa` }}
          </div>
        </div>
      </div>
    </div>

    <template v-if="canDeleteStage">
      <div class="csp-divider"></div>

      <div class="csp-section">
        <div class="csp-section-label csp-section-label-danger">⚠️ Zona peligrosa</div>
        <div class="csp-item csp-item-danger" @click="onDelete">
          <div class="csp-item-icon">🗑</div>
          <div class="csp-item-info">
            <div class="csp-item-title">Eliminar columna</div>
            <div class="csp-item-sub">Los {{ totalLeads }} {{ totalLeads === 1 ? 'lead' : 'leads' }} se moverán a otra etapa</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.csp-panel {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  width: 320px;
  background: linear-gradient(180deg, rgba(15,15,18,0.98), rgba(8,8,10,0.98));
  border: 1px solid rgba(251,191,36,0.35);
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.70), 0 0 0 1px rgba(251,191,36,0.08), 0 0 40px rgba(251,191,36,0.08);
  z-index: 50;
  overflow: hidden;
  animation: csp-slidein 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  color: #fff;
  font-family: -apple-system, system-ui, sans-serif;
}
.csp-panel::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(251,191,36,0.55), transparent);
}
.csp-panel::after {
  content: '';
  position: absolute;
  top: 18px;
  left: -8px;
  width: 8px;
  height: 1px;
  background: rgba(251,191,36,0.45);
}
@keyframes csp-slidein {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
}

.csp-close {
  position: absolute; top: 10px; right: 10px;
  width: 22px; height: 22px;
  border-radius: 6px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.55);
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  line-height: 1;
}
.csp-close:hover { background: rgba(239,68,68,0.10); color: #f87171; border-color: rgba(239,68,68,0.30); }

.csp-head {
  padding: 14px 16px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.csp-eyebrow { font-size: 9px; font-weight: 900; letter-spacing: 1.4px; color: #fbbf24; text-transform: uppercase; }
.csp-title {
  font-size: 14px; font-weight: 900; color: #fff;
  margin-top: 4px;
  display: flex; align-items: center; gap: 7px;
}
.csp-color-dot {
  width: 12px; height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
}
.csp-title-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.csp-sub { font-size: 10px; color: rgba(255,255,255,0.50); margin-top: 4px; }
.csp-position-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 8px;
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 999px;
  font-size: 9.5px;
  color: #fbbf24;
  font-weight: 800;
  margin-top: 6px;
}

.csp-section { padding: 8px 8px 4px; }
.csp-section-label {
  font-size: 8.5px;
  font-weight: 900;
  letter-spacing: 1.2px;
  color: rgba(251,191,36,0.85);
  text-transform: uppercase;
  padding: 4px 10px 6px;
}
.csp-section-label-danger { color: rgba(239,68,68,0.85); }

.csp-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 2px;
}
.csp-item:hover { background: rgba(251,191,36,0.06); }
.csp-item-disabled {
  opacity: 0.40;
  cursor: not-allowed;
}
.csp-item-disabled:hover { background: transparent; }

.csp-item-icon {
  width: 26px; height: 26px;
  border-radius: 7px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.csp-item-icon-color {
  background: rgba(239,68,68,0.18);
  border-color: rgba(239,68,68,0.40);
}

.csp-item-info { flex: 1; min-width: 0; }
.csp-item-title {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
}
.csp-item-sub {
  font-size: 9px;
  color: rgba(255,255,255,0.45);
  margin-top: 1px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.csp-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 6px 10px; }

.csp-item-danger:hover { background: rgba(239,68,68,0.10); }
.csp-item-danger .csp-item-title { color: #f87171; }
.csp-item-danger .csp-item-icon {
  background: rgba(239,68,68,0.10);
  border-color: rgba(239,68,68,0.30);
  color: #f87171;
}
</style>
